import { useCallback } from "react";
import { queries } from "../../shared/queries";
import type { DisconnectImpact } from "../components/DisconnectWarning";
import { groupRemovalDisconnects } from "../lib/groupRemovalDisconnects";
import { useGroupMemberships } from "./useGroupMemberships";
import { useQuery } from "./useQuery";

// Client-side preview of what removing members from a group will disconnect.
// Best-effort: RBAC-filtered queries can under-list, so the server drop stays
// authoritative.
export function useGroupRemovalDisconnectImpact(
  userGroupIds: readonly string[]
) {
  const enabled = userGroupIds.length > 0;
  const [connectorRows] = useQuery(
    enabled
      ? queries.groupConnectorLiveUsageByConnector({
          userGroupIds: [...userGroupIds],
        })
      : undefined
  );
  const [gitRows] = useQuery(
    enabled
      ? queries.groupConnectorGitRemoteUsage({
          userGroupIds: [...userGroupIds],
        })
      : undefined
  );
  const [spaces] = useQuery(queries.spaces({ settingsView: true }));
  const { userGroupIds: userGroupMemberships } = useGroupMemberships();

  return useCallback(
    (groupId: string, removedUserIds: readonly string[]): DisconnectImpact => {
      if (removedUserIds.length === 0)
        return { workflows: [], gitSpaceNames: [] };
      return groupRemovalDisconnects({
        groupId,
        removedUserIds,
        connectorRows: connectorRows ?? [],
        gitRows: gitRows ?? [],
        spaces: spaces ?? [],
        userGroupMemberships,
      });
    },
    [connectorRows, gitRows, spaces, userGroupMemberships]
  );
}
